import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { X509Certificates } from "@/services/partnerApi"
import type { X509Certificate } from "@/services/partnerApi"

const certificateQueryKeys = {
  all: ["certificates"] as const,
  detail: (id: string) => [...certificateQueryKeys.all, "detail", id] as const,
}

export function useCertificates(subscriberId?: string, name?: string) {
  return useQuery({
    queryKey: certificateQueryKeys.all,

    staleTime: 1000 * 60 * 5,

    queryFn: async () => {
      let certificates: Array<X509Certificate> = []
      const pagination = { offset: 0, limit: 200 }

      while (true) {
        const response = await X509Certificates.ListX509Certificates({
          pagination,
        })
        if (response.x509_certificates.length === 0) {
          break
        }
        certificates = certificates.concat(response.x509_certificates)
        if (response.x509_certificates.length < pagination.limit) {
          break
        }
        pagination.offset += response.x509_certificates.length
      }

      return certificates
    },

    select: (certificates) =>
      certificates.filter((cert) => {
        // If subscriberId is provided, only return certificates that match
        if (subscriberId && cert.subscriber_id !== subscriberId) {
          return false
        }

        // If name is provided, only return certificates that include the search value in the name or username
        if (name) {
          return cert.name?.toLowerCase().includes(name.toLowerCase())
        }

        // If no filters are provided, return all certificates
        return true
      }),
  })
}

export function useCertificate(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: certificateQueryKeys.detail(id),

    // The generated response types this field optional. Returning it straight
    // through means handing react-query an undefined, which it rejects with
    // "Query data cannot be undefined" and no clue which record was missing.
    queryFn: async () => {
      const { x509_certificate: certificate } =
        await X509Certificates.GetX509Certificate({ id })
      if (!certificate) {
        throw new Error(`No certificate returned for id ${id}`)
      }
      return certificate
    },

    initialData: () =>
      queryClient
        .getQueryData<Array<X509Certificate>>(certificateQueryKeys.all)
        ?.find((s) => s.id === id),

    initialDataUpdatedAt: () =>
      queryClient.getQueryState(certificateQueryKeys.all)?.dataUpdatedAt,
  })
}

export function useCreateCertificate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: X509Certificates.CreateX509Certificate,

    onMutate: async (newCertificate) => {
      await queryClient.cancelQueries({ queryKey: certificateQueryKeys.all })
      const previous = queryClient.getQueryData(certificateQueryKeys.all)
      queryClient.setQueryData<Array<X509Certificate>>(
        certificateQueryKeys.all,
        (old) => {
          const cert = { ...newCertificate, id: "new" }
          return old ? [...old, cert] : [cert]
        },
      )
      return { previous }
    },

    onError: (_err, _newCertificate, context) => {
      if (context) {
        queryClient.setQueryData(certificateQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: certificateQueryKeys.all })
    },
  })
}

export function useUpdateCertificate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: X509Certificates.UpdateX509Certificate,

    onMutate: async (newCertificate) => {
      await queryClient.cancelQueries({ queryKey: certificateQueryKeys.all })
      const previous = queryClient.getQueryData(certificateQueryKeys.all)
      queryClient.setQueryData<Array<X509Certificate>>(
        certificateQueryKeys.all,
        (old) => {
          return old?.map((s) =>
            s.id === newCertificate.id ? newCertificate : s,
          )
        },
      )
      return { previous }
    },

    onError: (_err, _newCertificate, context) => {
      if (context) {
        queryClient.setQueryData(certificateQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: certificateQueryKeys.all })
    },
  })
}

export function useDeleteCertificate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: X509Certificates.DeleteX509Certificate,

    onMutate: async (certificate) => {
      await queryClient.cancelQueries({ queryKey: certificateQueryKeys.all })
      await queryClient.removeQueries({
        queryKey: certificateQueryKeys.detail(certificate.id),
      })
      const previous = queryClient.getQueryData(certificateQueryKeys.all)
      queryClient.setQueryData<Array<X509Certificate>>(
        certificateQueryKeys.all,
        (old) => {
          return old?.filter((c) => c.id !== certificate.id)
        },
      )
      return { previous }
    },

    onError: (_err, _certificate, context) => {
      if (context) {
        queryClient.setQueryData(certificateQueryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: certificateQueryKeys.all })
    },
  })
}
